import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import './LegalPages.css';

const TermsConditions = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const en = (
    <>
      <p className="legal-updated">Last Updated: April 11, 2026</p>

      <h2>1. Acceptance of Terms</h2>
      <p>By accessing or using any services provided by Roolink ("Company," "we," "us"), including our website, client portal, hosting services, domain registration, application development, IT consultation, and AI solutions, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.</p>

      <h2>2. Services</h2>
      <h3>2.1 Web Hosting</h3>
      <ul>
        <li>Hosting services are provided on a subscription basis (monthly/annually)</li>
        <li>We guarantee 99.9% server uptime, excluding scheduled maintenance</li>
        <li>We reserve the right to suspend accounts that violate our Acceptable Use Policy</li>
        <li>Hosting accounts must not be used for illegal content, spam, phishing, or malware distribution</li>
        <li>Resource usage must remain within the limits of your selected plan</li>
        <li>We perform regular backups but clients are responsible for maintaining their own backup copies</li>
      </ul>

      <h3>2.2 Domain Registration</h3>
      <ul>
        <li>Domain registration is subject to ICANN policies and the respective registry's terms</li>
        <li>Domain registration fees are non-refundable once the domain is registered</li>
        <li>You must provide accurate WHOIS information as required by ICANN</li>
        <li>Domain renewals are the client's responsibility; we send reminder notifications but are not liable for expired domains</li>
        <li>Domain transfers are subject to registry lock periods and transfer fees</li>
      </ul>

      <h3>2.3 Application Development (Android & iOS)</h3>
      <ul>
        <li>Development projects are governed by individual project agreements/proposals</li>
        <li>Project timelines are estimates and may vary based on scope changes</li>
        <li>Clients must provide timely feedback and approvals at each development stage</li>
        <li>App Store and Google Play submission fees are the client's responsibility unless otherwise agreed</li>
        <li>We do not guarantee app approval by Apple or Google; rejection due to content policy violations is the client's responsibility</li>
        <li>Source code ownership is transferred to the client upon full payment unless otherwise specified</li>
        <li>Post-launch maintenance and updates require a separate support agreement</li>
      </ul>

      <h3>2.4 Web Development</h3>
      <ul>
        <li>Website development follows an agreed project scope and timeline</li>
        <li>Design revisions are limited to the number specified in the project agreement</li>
        <li>Content (text, images, branding) must be provided by the client unless content creation is included</li>
        <li>Third-party plugin/service licenses are the client's responsibility</li>
        <li>SEO optimization is included as specified in the project scope</li>
      </ul>

      <h3>2.5 IT Consultation</h3>
      <ul>
        <li>Consultation services are billed hourly or per project as agreed</li>
        <li>Recommendations are advisory in nature; implementation decisions are the client's responsibility</li>
        <li>Confidential information shared during consultation is protected under our NDA terms</li>
      </ul>

      <h3>2.6 Linux System Administration</h3>
      <ul>
        <li>Server management services cover agreed-upon tasks and response times</li>
        <li>Emergency support outside business hours may incur additional charges</li>
        <li>We are not liable for data loss due to client actions or third-party breaches</li>
        <li>Root/admin access sharing must follow security protocols</li>
      </ul>

      <h3>2.7 AI Solutions</h3>
      <ul>
        <li>AI integrations use third-party models (OpenAI, Anthropic Claude, Google Gemini) subject to their respective terms</li>
        <li>AI-generated content accuracy is not guaranteed; clients should verify critical outputs</li>
        <li>Data submitted to AI services may be processed by third-party AI providers</li>
        <li>Custom AI model training requires separate data processing agreements</li>
        <li>AI service availability depends on third-party provider uptime</li>
      </ul>

      <h2>3. Payment Terms</h2>
      <ul>
        <li>All prices are in Kuwaiti Dinar (KWD) unless otherwise stated</li>
        <li>Payment is due upon invoice generation unless credit terms are agreed</li>
        <li>We accept KNET, Visa, Mastercard, and bank transfer</li>
        <li>Late payments may result in service suspension after a 7-day grace period</li>
        <li>Development projects require milestone payments as specified in the project agreement</li>
        <li>Recurring services (hosting, domains) auto-renew unless cancelled before the renewal date</li>
      </ul>

      <h2>4. Refund Policy</h2>
      <ul>
        <li><strong>Hosting:</strong> 14-day money-back guarantee for new accounts. No refunds for renewals</li>
        <li><strong>Domains:</strong> Non-refundable once registered</li>
        <li><strong>Development:</strong> Refunds based on project milestones; completed milestones are non-refundable</li>
        <li><strong>Consultation:</strong> Non-refundable for completed sessions</li>
        <li><strong>AI Solutions:</strong> Non-refundable for delivered integrations</li>
      </ul>

      <h2>5. Intellectual Property</h2>
      <ul>
        <li>Custom development work is owned by the client upon full payment</li>
        <li>Pre-existing frameworks, libraries, and tools remain the property of their respective owners</li>
        <li>Roolink retains the right to showcase completed projects in our portfolio unless the client opts out in writing</li>
        <li>Client-provided content remains the client's property</li>
      </ul>

      <h2>6. Limitation of Liability</h2>
      <ul>
        <li>Roolink's total liability shall not exceed the amount paid for the specific service in the preceding 12 months</li>
        <li>We are not liable for indirect, incidental, or consequential damages</li>
        <li>We are not responsible for losses due to force majeure events</li>
        <li>Service downtime beyond our SLA guarantees will be compensated with service credits</li>
      </ul>

      <h2>7. Account Termination</h2>
      <ul>
        <li>Either party may terminate services with 30 days written notice</li>
        <li>We may immediately terminate accounts that violate these terms</li>
        <li>Upon termination, client data will be available for download for 30 days</li>
        <li>Outstanding payments remain due upon termination</li>
      </ul>

      <h2>8. Acceptable Use Policy</h2>
      <p>You agree not to use our services for:</p>
      <ul>
        <li>Any illegal activities or content</li>
        <li>Distributing malware, viruses, or malicious code</li>
        <li>Sending unsolicited bulk email (spam)</li>
        <li>Phishing or fraudulent activities</li>
        <li>Copyright infringement</li>
        <li>Activities that compromise server performance or security</li>
        <li>Cryptocurrency mining on shared hosting</li>
      </ul>

      <h2>9. Governing Law</h2>
      <p>These terms are governed by the laws of the State of Kuwait. Any disputes shall be resolved through the courts of Kuwait.</p>

      <h2>10. Contact</h2>
      <p>For questions about these terms:</p>
      <ul>
        <li>Email: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>Phone: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
      </ul>
    </>
  );

  const ar = (
    <>
      <p className="legal-updated">آخر تحديث: 11 أبريل 2026</p>

      <h2>1. قبول الشروط</h2>
      <p>باستخدامك لأي خدمات تقدمها رولينك ("الشركة" أو "نحن")، بما في ذلك موقعنا الإلكتروني وبوابة العملاء وخدمات الاستضافة وتسجيل النطاقات وتطوير التطبيقات والاستشارات التقنية وحلول الذكاء الاصطناعي، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق، يرجى عدم استخدام خدماتنا.</p>

      <h2>2. الخدمات</h2>
      <h3>2.1 استضافة المواقع</h3>
      <ul>
        <li>تُقدم خدمات الاستضافة على أساس اشتراك (شهري/سنوي)</li>
        <li>نضمن وقت تشغيل الخادم بنسبة 99.9%، باستثناء الصيانة المجدولة</li>
        <li>نحتفظ بالحق في تعليق الحسابات التي تنتهك سياسة الاستخدام المقبول</li>
        <li>يجب ألا تُستخدم حسابات الاستضافة للمحتوى غير القانوني أو البريد العشوائي أو التصيد أو توزيع البرمجيات الخبيثة</li>
        <li>يجب أن يبقى استخدام الموارد ضمن حدود خطتك المختارة</li>
        <li>نقوم بعمل نسخ احتياطية منتظمة لكن العملاء مسؤولون عن الاحتفاظ بنسخهم الاحتياطية</li>
      </ul>

      <h3>2.2 تسجيل النطاقات</h3>
      <ul>
        <li>تسجيل النطاق يخضع لسياسات ICANN وشروط السجل المعني</li>
        <li>رسوم تسجيل النطاق غير قابلة للاسترداد بمجرد تسجيل النطاق</li>
        <li>يجب تقديم معلومات WHOIS دقيقة كما هو مطلوب من ICANN</li>
        <li>تجديد النطاقات مسؤولية العميل؛ نرسل إشعارات تذكير لكننا غير مسؤولين عن النطاقات المنتهية</li>
        <li>نقل النطاقات يخضع لفترات القفل ورسوم النقل</li>
      </ul>

      <h3>2.3 تطوير التطبيقات (أندرويد و iOS)</h3>
      <ul>
        <li>تخضع مشاريع التطوير لاتفاقيات/عروض المشروع الفردية</li>
        <li>الجداول الزمنية للمشروع تقديرية وقد تتغير بناءً على تغييرات النطاق</li>
        <li>يجب على العملاء تقديم ملاحظات وموافقات في الوقت المناسب في كل مرحلة تطوير</li>
        <li>رسوم تقديم App Store و Google Play مسؤولية العميل ما لم يُتفق على خلاف ذلك</li>
        <li>لا نضمن قبول التطبيق من Apple أو Google؛ الرفض بسبب انتهاكات سياسة المحتوى مسؤولية العميل</li>
        <li>تُنقل ملكية الكود المصدري للعميل عند الدفع الكامل ما لم يُحدد خلاف ذلك</li>
        <li>الصيانة والتحديثات بعد الإطلاق تتطلب اتفاقية دعم منفصلة</li>
      </ul>

      <h3>2.4 تطوير الويب</h3>
      <ul>
        <li>يتبع تطوير المواقع نطاق وجدول زمني متفق عليهما</li>
        <li>مراجعات التصميم محدودة بالعدد المحدد في اتفاقية المشروع</li>
        <li>المحتوى (النصوص والصور والعلامة التجارية) يجب أن يُقدم من العميل ما لم يكن إنشاء المحتوى مشمولاً</li>
        <li>تراخيص الإضافات/الخدمات الخارجية مسؤولية العميل</li>
        <li>تحسين محركات البحث مشمول كما هو محدد في نطاق المشروع</li>
      </ul>

      <h3>2.5 الاستشارات التقنية</h3>
      <ul>
        <li>تُحتسب خدمات الاستشارة بالساعة أو لكل مشروع حسب الاتفاق</li>
        <li>التوصيات ذات طبيعة استشارية؛ قرارات التنفيذ مسؤولية العميل</li>
        <li>المعلومات السرية المشاركة أثناء الاستشارة محمية بموجب شروط اتفاقية عدم الإفصاح</li>
      </ul>

      <h3>2.6 إدارة أنظمة لينكس</h3>
      <ul>
        <li>تغطي خدمات إدارة الخوادم المهام المتفق عليها وأوقات الاستجابة</li>
        <li>الدعم الطارئ خارج ساعات العمل قد يترتب عليه رسوم إضافية</li>
        <li>لسنا مسؤولين عن فقدان البيانات بسبب إجراءات العميل أو اختراقات طرف ثالث</li>
        <li>مشاركة صلاحيات الروت/المسؤول يجب أن تتبع بروتوكولات الأمان</li>
      </ul>

      <h3>2.7 حلول الذكاء الاصطناعي</h3>
      <ul>
        <li>تستخدم تكاملات الذكاء الاصطناعي نماذج طرف ثالث (OpenAI، Anthropic Claude، Google Gemini) وتخضع لشروطها</li>
        <li>دقة المحتوى المولد بالذكاء الاصطناعي غير مضمونة؛ يجب على العملاء التحقق من المخرجات الحرجة</li>
        <li>البيانات المقدمة لخدمات الذكاء الاصطناعي قد تُعالج بواسطة مزودي ذكاء اصطناعي خارجيين</li>
        <li>تدريب نماذج ذكاء اصطناعي مخصصة يتطلب اتفاقيات معالجة بيانات منفصلة</li>
        <li>توفر خدمة الذكاء الاصطناعي يعتمد على وقت تشغيل المزود الخارجي</li>
      </ul>

      <h2>3. شروط الدفع</h2>
      <ul>
        <li>جميع الأسعار بالدينار الكويتي ما لم يُذكر خلاف ذلك</li>
        <li>الدفع مستحق عند إصدار الفاتورة ما لم يُتفق على شروط ائتمان</li>
        <li>نقبل كي نت وفيزا وماستركارد والتحويل البنكي</li>
        <li>التأخر في الدفع قد يؤدي إلى تعليق الخدمة بعد فترة سماح 7 أيام</li>
        <li>مشاريع التطوير تتطلب دفعات مراحل كما هو محدد في اتفاقية المشروع</li>
        <li>الخدمات المتكررة (الاستضافة والنطاقات) تتجدد تلقائياً ما لم تُلغَ قبل تاريخ التجديد</li>
      </ul>

      <h2>4. سياسة الاسترداد</h2>
      <ul>
        <li><strong>الاستضافة:</strong> ضمان استرداد الأموال خلال 14 يوماً للحسابات الجديدة. لا استرداد للتجديدات</li>
        <li><strong>النطاقات:</strong> غير قابلة للاسترداد بمجرد التسجيل</li>
        <li><strong>التطوير:</strong> الاسترداد بناءً على مراحل المشروع؛ المراحل المكتملة غير قابلة للاسترداد</li>
        <li><strong>الاستشارات:</strong> غير قابلة للاسترداد للجلسات المكتملة</li>
        <li><strong>حلول الذكاء الاصطناعي:</strong> غير قابلة للاسترداد للتكاملات المسلّمة</li>
      </ul>

      <h2>5. الملكية الفكرية</h2>
      <ul>
        <li>أعمال التطوير المخصصة مملوكة للعميل عند الدفع الكامل</li>
        <li>الأطر والمكتبات والأدوات الموجودة مسبقاً تبقى ملكاً لأصحابها</li>
        <li>تحتفظ رولينك بحق عرض المشاريع المكتملة في معرض أعمالنا ما لم يرفض العميل كتابياً</li>
        <li>المحتوى المقدم من العميل يبقى ملكاً للعميل</li>
      </ul>

      <h2>6. حدود المسؤولية</h2>
      <ul>
        <li>إجمالي مسؤولية رولينك لا يتجاوز المبلغ المدفوع للخدمة المحددة في الـ 12 شهراً السابقة</li>
        <li>لسنا مسؤولين عن الأضرار غير المباشرة أو العرضية أو التبعية</li>
        <li>لسنا مسؤولين عن الخسائر الناتجة عن أحداث القوة القاهرة</li>
        <li>توقف الخدمة بما يتجاوز ضمانات SLA سيُعوض بأرصدة خدمة</li>
      </ul>

      <h2>7. إنهاء الحساب</h2>
      <ul>
        <li>يمكن لأي طرف إنهاء الخدمات بإشعار كتابي قبل 30 يوماً</li>
        <li>يمكننا إنهاء الحسابات التي تنتهك هذه الشروط فوراً</li>
        <li>عند الإنهاء، ستكون بيانات العميل متاحة للتحميل لمدة 30 يوماً</li>
        <li>المدفوعات المستحقة تبقى واجبة عند الإنهاء</li>
      </ul>

      <h2>8. سياسة الاستخدام المقبول</h2>
      <p>توافق على عدم استخدام خدماتنا لـ:</p>
      <ul>
        <li>أي أنشطة أو محتوى غير قانوني</li>
        <li>توزيع البرمجيات الخبيثة أو الفيروسات</li>
        <li>إرسال بريد إلكتروني جماعي غير مرغوب فيه (سبام)</li>
        <li>التصيد الاحتيالي أو الأنشطة الاحتيالية</li>
        <li>انتهاك حقوق النشر</li>
        <li>الأنشطة التي تهدد أداء أو أمان الخادم</li>
        <li>تعدين العملات الرقمية على الاستضافة المشتركة</li>
      </ul>

      <h2>9. القانون الحاكم</h2>
      <p>تخضع هذه الشروط لقوانين دولة الكويت. تُحل أي نزاعات من خلال محاكم الكويت.</p>

      <h2>10. اتصل بنا</h2>
      <p>للأسئلة حول هذه الشروط:</p>
      <ul>
        <li>البريد الإلكتروني: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>الهاتف: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
      </ul>
    </>
  );

  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-header-bg">
          <div className="legal-shape ls1"></div>
          <div className="legal-shape ls2"></div>
        </div>
        <div className="container">
          <a href="/" className="legal-back"><FiArrowLeft /> {isAr ? 'الرئيسية' : 'Home'}</a>
          <h1>{isAr ? 'الشروط والأحكام' : 'Terms & Conditions'}</h1>
          <p>Roolink</p>
        </div>
      </div>
      <motion.div
        className="container legal-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {isAr ? ar : en}
      </motion.div>
    </div>
  );
};

export default TermsConditions;
